import Dialog from '@pelckmans/elementis/components/dialog';
import RadioGroup, { Radio } from '@pelckmans/elementis/components/radio-group';
import TextField from '@pelckmans/elementis/components/text-field';
import { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { SmartschoolSchool, syncAccount } from '../../../api/my-account';
import ModalContext from '../../../contexts/ModalContext';
import Button from '@pelckmans/elementis/components/button';
import { updateSmartschoolOneRoster } from '../api/smartschool-api';
import { useSearchParams } from 'react-router-dom';
import AccountContext from '../../../contexts/AccountContext';
import { AxiosError } from 'axios';
import Alert from '@pelckmans/elementis/components/alert';
import { buildGroupProps } from '../../../utils/elementisUtils';
import { useDefaultToast } from '../../../hooks/useDefaultToast';

const translationKey = 'PAGES.PROFILE.MY_DETAILS.LINKED_ACCOUNTS.CONFIGURE_ONEROSTER_MODAL';

export interface Props {
  id: string;
  school: SmartschoolSchool;
}

const WEB_ACCESS_TEMPLATE_URL = 'https://:domain/ims/oneroster/v1p1';
const TOKEN_TEMPLATE_URL = 'https://:domain/ims/oneroster/token';

interface FormValues {
  enabled: boolean;
  clientId: string;
  clientSecret: string;
}

export default function ConfigureOneRosterModal({ id, school }: Props) {
  const form = useForm<FormValues>({
    defaultValues: {
      enabled: school.oneRoster?.enabled || false,
      clientId: school.oneRoster?.clientId || '',
      clientSecret: school.oneRoster?.clientSecret || '',
    },
    mode: 'onBlur',
  });

  const { refreshAccount } = useContext(AccountContext);

  const [searchParams, setSearchParams] = useSearchParams();
  const [alert, setAlert] = useState<string | undefined>(undefined);

  const { t } = useTranslation();
  const { closeModal } = useContext(ModalContext);

  const { successToast, failedToast } = useDefaultToast();

  const handleClose = () => {
    closeModal(id);
  };

  const validationCode = searchParams.get('code');

  const onSubmit = async (data: FormValues) => {
    try {
      await updateSmartschoolOneRoster(school.id, { ...data, validationCode: validationCode! });

      handleClose();
      setSearchParams(prev => {
        prev.delete('code');
        prev.delete('openSmsModal');
        return prev;
      });

      await syncAccount();
      await refreshAccount();

      successToast();
    } catch (error) {
      if (
        error instanceof AxiosError &&
        error.response?.status === 400 &&
        error.response.data?.message === 'INVALID_ONE_ROSTER_CREDENTIALS'
      ) {
        return setAlert(t(`${translationKey}.FORM.INVALID_CREDENTIAL_ALERT`));
      }

      failedToast();
    }
  };

  return (
    <Dialog id={id} modal size="sm" aria-label={t(`${translationKey}.A11Y_LABEL`)} onClose={handleClose}>
      <Dialog.Header title={t(`${translationKey}.TITLE`)} />
      <Dialog.Body>
        {alert && <Alert prominence="danger">{alert}</Alert>}
        <p>
          <Trans
            i18nKey={`${translationKey}.DESCRIPTION`}
            components={{
              // TODO: Link component + sizing??
              // eslint-disable-next-line jsx-a11y/anchor-has-content
              a: <a href={process.env.REACT_APP_ONEROSTER_CONFIGURATION_LINK} />,
            }}
          />
        </p>
        <form id="form-oneroster-settings" className="form-oneroster-settings" onSubmit={form.handleSubmit(onSubmit)}>
          <RadioGroup
            direction="horizontal"
            label={t(`${translationKey}.FORM.ENABLED_LABEL`)}
            onChange={(value: string) => form.setValue('enabled', value === 'true')}
            value={form.watch('enabled').toString()}
          >
            <Radio value="false">{t(`${translationKey}.FORM.DISABLED_RADIO_LABEL`)}</Radio>
            <Radio value="true">{t(`${translationKey}.FORM.ENABLED_RADIO_LABEL`)}</Radio>
          </RadioGroup>
          <TextField
            labelText={t(`${translationKey}.FORM.CLIENT_ID.LABEL`)}
            required
            error={!!form.formState.errors.clientId}
            errorText={form.formState.errors.clientId?.message}
            {...form.register('clientId', {
              required: t(`${translationKey}.FORM.CLIENT_ID.REQUIRED`),
            })}
          />
          <TextField
            labelText={t(`${translationKey}.FORM.CLIENT_SECRET.LABEL`)}
            required
            error={!!form.formState.errors.clientSecret}
            errorText={form.formState.errors.clientSecret?.message}
            {...form.register('clientSecret', {
              required: t(`${translationKey}.FORM.CLIENT_SECRET.REQUIRED`),
            })}
          />
          <TextField
            labelText={t(`${translationKey}.FORM.WEB_ACCESS_URL.LABEL`)}
            required
            disabled
            value={WEB_ACCESS_TEMPLATE_URL.replace(':domain', school.domain)}
          />
          <TextField
            labelText={t(`${translationKey}.FORM.TOKEN_URL.LABEL`)}
            required
            disabled
            value={TOKEN_TEMPLATE_URL.replace(':domain', school.domain)}
          />
        </form>
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsRight={buildGroupProps(
          <>
            <Button prominence="neutral" onClick={handleClose} key="cancel">
              {t('ACTIONS.CANCEL')}
            </Button>
            <Button prominence="success" type="submit" form="form-oneroster-settings" key="submit">
              {t('ACTIONS.CONFIRM')}
            </Button>
          </>,
        )}
      />
    </Dialog>
  );
}
